import {
    SliceFactory,
    FetchCollectionData,
    ApiCollectionResponse,
    FetchItemDataType,
} from '@europrocurement/l2d-redux-utils';

import {
    Configuration,
    ConfigurationParameters,
    TachesEtRappelsApi,
    TachesEtRappelsApiGetCollectionTachesEtRappelsCollectionRequest,
    TachesEtRappelsApiGetTachesEtRappelsItemRequest,
    TachesEtRappelsJsonldTacheRead,
} from '@europrocurement/l2d-domain/openApi/ApiTiers';
import { PRESCRIPEUR_TACHES_SLICE_NAME, TIERS_REDUCER_NAME } from '../constants';

export type PrescripteurTachesApiObject = TachesEtRappelsJsonldTacheRead & Record<string, unknown>;

export type PrescripteurTaches = PrescripteurTachesApiObject;

export const mapperPrescripteurTaches = function (taches: PrescripteurTaches) {
    return {
        ...taches,
    } as PrescripteurTaches;
};

type dateFilters = {
    startDate?: string;
    endDate?: string;
};

export function createPrescripteurTachesSlice(configuration: ConfigurationParameters) {
    const conf = new Configuration(configuration);
    const api = new TachesEtRappelsApi(conf);

    const fetchPrescripteurTachesCollection: FetchCollectionData<PrescripteurTaches> = ({
        pagination,
        filters,
        orders,
        xIdSociete = 12,
    }) => {
        const {
            tacheTitle,
            tacheIsDone,
            tacheAssignTo,
            tacheCreatedBy,
            tacheExpiredAt,
            tacheCreatedAt,
            tachePriority,
            tachePrescripteurIri,
        } = filters;

        let tacheExpiredAtAfter: string | undefined;
        let tacheExpiredAtBefore: string | undefined;
        let tacheCreatedAtAfter: string | undefined;
        let tacheCreatedAtBefore: string | undefined;

        let orderTiersRaisonSociale: 'asc' | 'desc' | undefined;
        let orderTacheAssignToPrenom: 'asc' | 'desc' | undefined;
        let orderTacheIsDone: 'asc' | 'desc' | undefined;
        let orderTacheTitle: 'asc' | 'desc' | undefined;
        let orderTacheCreatedAt: 'asc' | 'desc' | undefined;
        let orderTacheExpiredAt: 'asc' | 'desc' | undefined;

        // Range of date to filter
        if (tacheExpiredAt) {
            const filtersDate = tacheExpiredAt as dateFilters;
            const startedAt = filtersDate.startDate && new Date(filtersDate.startDate);
            if (startedAt) {
                startedAt.setHours(0, 0);
                tacheExpiredAtAfter = startedAt.toISOString();
            }

            const endedAt = filtersDate.endDate && new Date(filtersDate.endDate);
            if (endedAt) {
                endedAt.setHours(23, 59);
                tacheExpiredAtBefore = endedAt.toISOString();
            }
        }

        // Range of date to filter
        if (tacheCreatedAt) {
            const filtersDate = tacheCreatedAt as dateFilters;
            const startedAt = filtersDate.startDate && new Date(filtersDate.startDate);
            if (startedAt) {
                startedAt.setHours(0, 0);
                tacheCreatedAtAfter = startedAt.toISOString();
            }

            const endedAt = filtersDate.endDate && new Date(filtersDate.endDate);
            if (endedAt) {
                endedAt.setHours(23, 59);
                tacheCreatedAtBefore = endedAt.toISOString();
            }
        }

        orders.forEach((order) => {
            if (order.field === 'tiersRaisonSociale') {
                orderTiersRaisonSociale = order.value;
            }
            if (order.field === 'tacheAssignTo') {
                orderTacheAssignToPrenom = order.value;
            }
            if (order.field === 'tacheIsDone') {
                orderTacheIsDone = order.value;
            }
            if (order.field === 'tacheTitle') {
                orderTacheTitle = order.value;
            }
            if (order.field === 'tacheCreatedAt') {
                orderTacheCreatedAt = order.value;
            }
            if (order.field === 'tacheExpiredAt') {
                orderTacheExpiredAt = order.value;
            }
        });

        const requestParameters: TachesEtRappelsApiGetCollectionTachesEtRappelsCollectionRequest = {
            page: pagination.page + 1,
            // Filters
            tiers: tachePrescripteurIri as string,
            tacheIsDone: tacheIsDone as boolean,
            tacheTitle: tacheTitle as string,
            tacheAssignTo: tacheAssignTo as string,
            tacheCreatedBy: tacheCreatedBy as string,
            tachePriority: tachePriority as number,
            tacheExpiredAtAfter,
            tacheExpiredAtBefore,
            tacheCreatedAtAfter,
            tacheCreatedAtBefore,
            // Order
            orderTacheAssignToPrenom,
            orderTacheIsDone,
            orderTacheTitle,
            orderTacheCreatedAt,
            orderTiersRaisonSociale,
            orderTacheExpiredAt,
            xIdSociete,
        };

        return api
            .getCollectionTachesEtRappelsCollection(requestParameters, undefined)
            .then((res) => res.data) as Promise<ApiCollectionResponse<PrescripteurTaches>>;
    };

    const fetchPrescripteurTachesItem: FetchItemDataType<PrescripteurTaches> = ({
        idItem,
        xIdSociete,
    }) => {
        const requestParameters: TachesEtRappelsApiGetTachesEtRappelsItemRequest = {
            id: String(idItem),
            xIdSociete,
        };

        return api
            .getTachesEtRappelsItem(requestParameters)
            .then((res) => res.data) as Promise<PrescripteurTaches>;
    };

    const {
        slice: prescripteurTachesSlice,
        dataSourcesThunks: prescripteurTachesDataSourcesThunks,
    } = SliceFactory.createSlice<PrescripteurTaches, PrescripteurTaches>(
        PRESCRIPEUR_TACHES_SLICE_NAME,
        TIERS_REDUCER_NAME,
        [],
        fetchPrescripteurTachesCollection,
        fetchPrescripteurTachesItem,
        mapperPrescripteurTaches,
        {},
        undefined,
        [{ field: 'tacheIsDone', value: 'asc' }],
    );

    return { prescripteurTachesSlice, prescripteurTachesDataSourcesThunks };
}
